import { useEffect, useState, useRef, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { DataGrid } from "@mui/x-data-grid"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box
} from "@mui/material"
import { DataGridStyles, DataGridWrapperStyles, DialogStyles } from "../styles/MaintenanceStyles"
import {
    getFrequencies,
    updateFrequency,
    updateselected,
    updateIsBeingEdited,
    setAlertDialog,
    setAlertText
} from "../../../actions/maintenanceActions"
import {
    toColumns,
    computeFrequencyMutation,
    useFrequencyMutation
} from "../MaintenanceSharedFunctions"
import FrequencyToolBar from "./FrequencyToolBar"
import axios from "../../../auth/axiosAuth"
import { backendURL } from "../../../config"

export default function Frequency() {
    const dispatch = useDispatch()
    const mutateRow = useFrequencyMutation()
    const noButtonRef = useRef(null)

    // Store Variables
    const maintenance = useSelector((state) => state.maintenance)
    const auth = useSelector((state) => state.auth)
    const loggedIn = useSelector(
        (state) => state.auth.user && state.auth.user.auth
    )
    const permissions = useSelector((state) => state.auth.permissions)

    const [promiseArguments, setPromiseArguments] = useState(null)

    useEffect(() => {
        if (loggedIn) {
            async function runUseEffectFunctions() {
                await dispatch(getFrequencies())
            }
            runUseEffectFunctions()
        }
    }, [dispatch, loggedIn])

    //creating columns and rows for the grid
    let columns = []
    let rows = []
    const frequencies = maintenance.frequencies
    if (frequencies?.length > 0) {
        columns = toColumns(Object.keys(frequencies[0]), permissions)
        rows = frequencies
    }

    //handling the grid's row update
    const processRowUpdate = useCallback(
        (newRow, oldRow) =>
            new Promise((resolve, reject) => {
                const mutation = computeFrequencyMutation(newRow, oldRow)
                if (mutation) {
                    // Save the arguments to resolve or reject the promise later
                    setPromiseArguments({ resolve, reject, newRow, oldRow })
                } else {
                    resolve(oldRow) // Nothing was changed
                }
            }),
        []
    )

    //handling error in row update
    const onProcessRowUpdateError = () => {
        if (promiseArguments) {
            promiseArguments.reject()
            setPromiseArguments(null)
        }
    }

    //handle closing confirmation dialog on no
    const handleCancelUpdateFrequency = () => {
        const { oldRow, resolve } = promiseArguments
        resolve(oldRow) // Resolve with the old row to not update the internal state
        setPromiseArguments(null)
        dispatch(setAlertDialog(false))
        dispatch(updateselected([]))
        dispatch(updateIsBeingEdited(false))
    }

    //handle closing confirmation dialog on yes
    const handleConfirmUpdateFrequency = async () => {
        const { newRow, oldRow, reject, resolve } = promiseArguments
        const res = await axios.get(
            `${backendURL}/frequencies/check/${oldRow.FrequencyId}`
        )
        if (res.data.length > 0) {
            dispatch(
                setAlertText(
                    "This frequency is being used and cannot be updated"
                )
            )
            dispatch(setAlertDialog(true))
            const { oldRow, resolve } = promiseArguments
            resolve(oldRow) // Resolve with the old row to not update the internal state
            setPromiseArguments(null)
            dispatch(updateselected([]))
            dispatch(updateIsBeingEdited(false))
            return
        }
        if (newRow.Frequency.trim() !== "") {
            if (
                maintenance.frequencies?.filter(
                    (frequency) =>
                        frequency.Frequency.toLowerCase() ===
                        newRow.Frequency.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This frequency already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    // Make the HTTP request to save in the backend
                    const response = await mutateRow(newRow)
                    const newFrequency = {
                        Frequency: response,
                        AuditLog: {
                            FrequencyId: response.FrequencyId,
                            Action: "Update Frequency",
                            OldValue: oldRow.Frequency,
                            NewValue: newRow.Frequency,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    // Update frequency and refresh store data
                    await dispatch(updateFrequency(newFrequency))
                    await dispatch(getFrequencies())
                    await dispatch(updateselected([]))
                    await dispatch(updateIsBeingEdited(false))
                    // Resolve with the new row to update the internal state
                    resolve(response)
                    // Clear the promise arguments to avoid resolving or rejecting the same promise twice
                    setPromiseArguments(null)
                } catch (error) {
                    reject(oldRow)
                    console.log(error)
                    setPromiseArguments(null)
                    await dispatch(updateselected([]))
                    await dispatch(updateIsBeingEdited(false))
                }
            }
        } else {
            reject(oldRow)
            setPromiseArguments(null)
        }
    }

    //update store when clicking on the row
    const handleRowClick = (event, row) => {
        dispatch(updateselected(event.row))
    }

    const handleRowEdit = async (row) => {
        await dispatch(updateIsBeingEdited(true))
    }

    const handleRowEditStop = async (row) => {
        await dispatch(updateIsBeingEdited(false))
    }

    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    }

    // Handles rendering the dialog to confirm updating a frequency
    const renderConfirmUpdateFrequencyDialog = () => {
        if (!promiseArguments) {
            return null
        }

        const { newRow, oldRow } = promiseArguments
        const mutation = computeFrequencyMutation(newRow, oldRow)

        return (
            <Dialog
                sx={DialogStyles}
                maxWidth="xs"
                TransitionProps={{ onEntered: handleEntered }}
                open={!!promiseArguments}
                onClose={handleCancelUpdateFrequency}
            >
                <DialogTitle sx={DialogStyles}>
                    Are you sure?
                </DialogTitle>
                <DialogContent dividers>
                    {`Pressing 'Yes' will change ${mutation}.`}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmUpdateFrequency}
                        sx={DialogStyles}
                    >
                        Yes
                    </Button>
                    <Button
                        ref={noButtonRef}
                        onClick={handleCancelUpdateFrequency}
                        sx={DialogStyles}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Box sx={DataGridWrapperStyles}>
            {renderConfirmUpdateFrequencyDialog()}
            <DataGrid
                getRowId={(row) => row.FrequencyId}
                rows={rows}
                columns={columns}
                columnVisibilityModel={{
                    FrequencyId: false
                }}
                processRowUpdate={processRowUpdate}
                onRowClick={handleRowClick}
                onProcessRowUpdateError={onProcessRowUpdateError}
                experimentalFeatures={{ newEditingApi: true }}
                slots={{ toolbar: FrequencyToolBar }}
                showToolbar
                sx={DataGridStyles}
                onCellEditStart={handleRowEdit}
                onCellEditStop={handleRowEditStop}
            />
        </Box>
    )
}
